<script lang="ts" setup>
import { SancareDateRangePicker, SancareMultiselect } from '@sancare/typescript-commons'
import { MultiSelectItem } from '@sancare/typescript-commons/src/components/types'
import { Dayjs } from 'dayjs'
import { computed } from 'vue'

import { createMulticheckOptions } from '@/filters/utils'
import { HospitalizationTypeFullName, HospitalizationTypeIcon } from '@/misc/constants'
import { useStore } from '@/store'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()

withDefaults(defineProps<{
  earlyLimit: Dayjs | null
  lateLimit: Dayjs | null
  medicalUnits: MultiSelectItem[]
  selectedMedicalUnits: number[]
  medicalUnitsRequest: { fetching: boolean }
  showMedicalUnitsFilter?: boolean
}>(), {
  showMedicalUnitsFilter: true,
})

const emit = defineEmits<{
  'update:earlyLimit': [limit: Dayjs | null]
  'update:lateLimit': [limit: Dayjs | null]
  'update:dateRange': [range: { earlyLimit: Dayjs | null, lateLimit: Dayjs | null }]
  'update:selectedMedicalUnits': [medicalUnitList: number[]]
  'update:hospitalizationTypes': [types: number[]]
}>()

const pmsiType = computed(() => store.state.settings.pmsi.pmsiType)
const multicheckHospitalizationType = computed(() => createMulticheckOptions(
  HospitalizationTypeFullName,
  true,
  HospitalizationTypeIcon
))
const hospitalizationTypes = computed(() => store.state.settings.hospitalizationTypes)

function setDateRange(earlyLimit: Dayjs | null, lateLimit: Dayjs | null) {
  emit('update:earlyLimit', earlyLimit)
  emit('update:lateLimit', lateLimit)
  emit('update:dateRange', { earlyLimit, lateLimit })
}

function updateMedicalUnits(medicalUnitList: number[]) {
  emit('update:selectedMedicalUnits', medicalUnitList)
}

function updateHospitalizationTypes(types: number[]) {
  emit('update:hospitalizationTypes', types)
}
</script>

<template>
  <div class="col-12 col-lg-4">
    <div class="row px-2 py-2 align-items-center flex-nowrap justify-content-start">
      <div class="col-auto px-2">
        <sancare-date-range-picker
          :start-date="earlyLimit"
          :end-date="lateLimit"
          @update:range="setDateRange"
        />
      </div>
    </div>
  </div>
  <div
    v-if="showMedicalUnitsFilter !== false"
    class="col-12 col-lg-3 my-3 my-lg-0 text-lg-center d-sm-flex justify-content-center"
  >
    <sancare-multiselect
      :title="$t('global.medicalUnits')"
      :selected-items="selectedMedicalUnits"
      :items="medicalUnits"
      :is-ready="!medicalUnitsRequest.fetching"
      :none-label="$t('all.feminine')"
      :all-label="$t('all.feminine')"
      @update-selection="updateMedicalUnits"
    />
  </div>

  <slot name="additional-filters" />

  <div
    v-if="pmsiType === PmsiTypeEnum.SMR"
    class="col-12 col-lg-4 my-3"
  >
    <sancare-multiselect
      :title="$t('hospitalizationTypes')"
      :selected-items="hospitalizationTypes"
      :items="multicheckHospitalizationType"
      @update-selection="updateHospitalizationTypes"
    />
  </div>
</template>
